import React from 'react'
import { Box, DialogContent } from '@mui/material'
import {
  AddPatientButton,
  BottomCardInfo,
  EditStaff,
  EmptyRoomBox,
  ExpectedEnd,
  MoreNursesChip,
  NurseChip,
  NursesSection,
  RoomGridContainer,
  RoomGridContent,
  RoomHeader,
  RoomName,
  StatusChip,
  StatusDot,
  StyledGrid,
  StyledMenu,
  StyledMenuItem,
  StyledPaper,
  StyledPopover,
  PopoverContent,
  PopoverTitle,
  StaffList,
  StaffItem,
  StaffStatusDot,
  StaffName,
  StyledDialog,
  StyledDialogTitle,
  DialogTitleText,
  StyledTextField,
  StyledAutocompleteChip,
  StyledAutocomplete,
  StyledDialogActions,
  CancelButton,
  SaveButton,
  ExpectedEndIcon,
  NoRoomsText,
  NoRoomsContainer,
} from './RoomGrid.styles'
import { useRoomGridLogic } from './RoomGrid.logic'
import {
  calculateSurgeryExpectedEndTime,
  getStatusColor,
  getStatusLabel,
} from '../helpers'
import PatientCard from '../PatientCard/PatientCard'
import { EPatientStatus } from '../RoomView.types'
import AddPatientMenu from '../AddPatientMenu'

const RoomGrid: React.FC = () => {
  const logic = useRoomGridLogic()

  if (logic.rooms.length === 0)
    return (
      <NoRoomsContainer>
        <NoRoomsText>NO ROOMS FOUND</NoRoomsText>
        <AddPatientButton
          variant="contained"
          size="large"
          onClick={logic.handleAddRoomsClick}
        >
          ADD ROOMS
        </AddPatientButton>
      </NoRoomsContainer>
    )

  return (
    <RoomGridContainer>
      <RoomGridContent container spacing={0.5}>
        {logic.rooms.map((room, index) => {
          const surgery = logic.scheduledSurgeries.find(
            (surgery) => surgery._id === room.procedureId
          )
          return (
            <StyledGrid key={room.id}>
              <StyledPaper
                $hasStaff={
                  logic.roomViewSettings.showStaffAssignment &&
                  logic.surgeryStaff.length > 0
                }
                $hasEndTime={logic.roomViewSettings.showExpectedEndTime}
              >
                <Box>
                  <RoomHeader>
                    <RoomName>
                      <StatusDot $isGreen={!!surgery} />
                      {room.name}
                    </RoomName>
                    {surgery && (
                      <StatusChip
                        $bgColor={getStatusColor(
                          surgery.patientStatusHistory[0]?.status,
                          logic.patientStatusOptions
                        )}
                        label={`✎ ${getStatusLabel(
                          surgery?.patientStatusHistory[0]?.status,
                          logic.patientStatusOptions
                        )}`}
                        onClick={(e) => logic.handleStatusClick(e, surgery._id)}
                      />
                    )}
                  </RoomHeader>
                  {surgery && <PatientCard surgery={surgery} />}
                </Box>

                {surgery ? (
                  <BottomCardInfo>
                    {logic.roomViewSettings.showStaffAssignment &&
                      logic.surgeryStaff.length > 0 && (
                        <NursesSection>
                          <EditStaff
                            onClick={logic.handleEditStaff(surgery, index)}
                          >
                            Staff ✎
                          </EditStaff>
                          {surgery.surgeryStaff.length > 0 && (
                            <NurseChip
                              key={surgery.surgeryStaff[0]._id || 0}
                              label={surgery.surgeryStaff[0].firstName}
                              size="small"
                            />
                          )}
                          {surgery.surgeryStaff.length > 1 && (
                            <NurseChip
                              key={surgery.surgeryStaff[1]._id || 1}
                              label={surgery.surgeryStaff[1].firstName}
                              size="small"
                            />
                          )}
                          {surgery.surgeryStaff.length > 2 && (
                            <MoreNursesChip
                              label={`+${surgery.surgeryStaff.length - 2}`}
                              size="small"
                              onClick={(e) => logic.handleStaffClick(e, index)}
                            />
                          )}
                        </NursesSection>
                      )}

                    {surgery.procedure.duration &&
                      logic.roomViewSettings.showExpectedEndTime && (
                        <ExpectedEnd>
                          <ExpectedEndIcon />
                          Expected end:{' '}
                          {calculateSurgeryExpectedEndTime(surgery)}
                        </ExpectedEnd>
                      )}
                  </BottomCardInfo>
                ) : (
                  <EmptyRoomBox>
                    <AddPatientButton
                      variant="contained"
                      onClick={(e) => logic.handleAddPatientClick(e, index)}
                      size="small"
                    >
                      Add Patient
                    </AddPatientButton>
                  </EmptyRoomBox>
                )}
              </StyledPaper>
            </StyledGrid>
          )
        })}
      </RoomGridContent>

      <StyledMenu
        anchorEl={logic.statusAnchorEl}
        open={Boolean(logic.statusAnchorEl)}
        onClose={logic.handleStatusClose}
      >
        {Object.values(EPatientStatus).map((status) => (
          <StyledMenuItem
            key={status}
            disabled={logic.selectedSurgeryInfo?.initialStatus === status}
            onClick={logic.handleStatusChange(status)}
            $highlight={logic.shouldHighlightStatus(status)}
          >
            {getStatusLabel(status, logic.patientStatusOptions)}
          </StyledMenuItem>
        ))}
      </StyledMenu>

      <StyledPopover
        open={Boolean(logic.staffPopoverAnchor)}
        anchorEl={logic.staffPopoverAnchor}
        onClose={logic.handleStaffPopoverClose}
      >
        <PopoverContent>
          <PopoverTitle variant="subtitle2">Staff</PopoverTitle>
          <StaffList>
            {logic.selectedSurgeryStaff &&
              logic.selectedSurgeryStaff.map((staff, index) => (
                <StaffItem key={index}>
                  <StaffStatusDot />
                  <StaffName variant="body2">
                    {staff.firstName} {staff.lastName}
                  </StaffName>
                </StaffItem>
              ))}
          </StaffList>
        </PopoverContent>
      </StyledPopover>

      <StyledDialog
        open={logic.editStaffDialogOpen}
        onClose={() => {
          logic.setEditStaffDialogOpen(false)
          logic.setSelectedStaffForEdit({
            initialStaff: [],
            newStaff: [],
          })
        }}
        maxWidth="xs"
        fullWidth
      >
        <StyledDialogTitle>
          <DialogTitleText variant="h6">Edit Staff Team</DialogTitleText>
        </StyledDialogTitle>
        <DialogContent>
          <StyledAutocomplete
            multiple
            options={logic.availableSurgeryStaff}
            value={logic.selectedStaffForEdit.newStaff}
            onChange={(_event, newValue) => {
              logic.handleStaffSelectionChange(newValue)
            }}
            getOptionLabel={(option) =>
              typeof option === 'string'
                ? option
                : `${option.firstName} ${option.lastName}${
                    option.roleDescription ? ` (${option.roleDescription})` : ''
                  }`
            }
            isOptionEqualToValue={(option, value) =>
              typeof option === 'string'
                ? option === value
                : option._id === value._id
            }
            renderInput={(params) => (
              <StyledTextField
                {...params}
                variant="outlined"
                placeholder="Select staff members"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <StyledAutocompleteChip
                  {...getTagProps({ index })}
                  key={typeof option === 'string' ? option : option._id}
                  label={
                    typeof option === 'string'
                      ? option
                      : `${option.firstName} ${option.lastName}`
                  }
                  size="small"
                />
              ))
            }
          />
        </DialogContent>
        <StyledDialogActions>
          <CancelButton
            onClick={logic.handleCancelStaffEdit}
            disabled={logic.isLoading}
          >
            Cancel
          </CancelButton>
          <SaveButton
            variant="contained"
            onClick={logic.handleSaveStaff}
            disabled={logic.isLoading}
          >
            {logic.isLoading ? 'Saving...' : 'Save Changes'}
          </SaveButton>
        </StyledDialogActions>
      </StyledDialog>

      <AddPatientMenu
        anchorEl={logic.addPatientAnchorEl}
        onClose={logic.handleAddPatientClose}
        roomName={logic.rooms[logic.selectedRoomIndex ?? 0]?.name}
        status={EPatientStatus.ASSIGN_ROOM}
        roomId={logic.selectedRoomId}
        showTabs
      />
    </RoomGridContainer>
  )
}

export default RoomGrid
